import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import { getBoardCellSizeFromArea, getBoardFontSizeFromArea } from '../../utils/gameLayout';

interface FillominoPuzzle {
  width: number;
  height: number;
  clues: number[]; // 0 = vacío, 1-9 = pista fija dada
  solution: number[];
  seed: number;
  difficulty: number;
}

const difficulties = [
  { orden: 1, nombre: 'Neurona', icono: '🧠' },
  { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
  { orden: 3, nombre: 'Cerebro', icono: '🧩' },
  { orden: 4, nombre: 'Genio', icono: '💡' },
  { orden: 5, nombre: 'Einstein', icono: '🎓' },
];

function getNeighbors(index: number, width: number, height: number): number[] {
  const row = Math.floor(index / width);
  const col = index % width;
  const res: number[] = [];
  for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
    const nr = row + dr, nc = col + dc;
    if (nr >= 0 && nr < height && nc >= 0 && nc < width) res.push(nr * width + nc);
  }
  return res;
}

/**
 * Analiza el tablero actual: agrupa en componentes conexas de mismo
 * número, y clasifica cada componente en "error" (se resalta en rojo,
 * como pide la regla del juego) o "cerrada y correcta" (tamaño exacto —
 * se le dibuja un borde negro delimitando el grupo, tal y como se
 * complete).
 *
 * Un grupo demasiado grande (tamaño > valor) es siempre un error, esté
 * "cerrado" o no — añadir más casillas nunca lo arregla. Un grupo del
 * tamaño exacto es siempre correcto, se marque cuando se marque. Pero
 * un grupo demasiado pequeño solo es un error cuando está "cerrado"
 * (ninguna de sus casillas tiene ya un vecino vacío, así que no puede
 * crecer más): mientras siga abierto, todavía podría completarse.
 *
 * Antes solo se detectaba el caso "se pasó de tamaño"; un grupo cerrado
 * y corto (p. ej. un "4" que ya no puede ampliarse pero solo tiene 3
 * casillas) se quedaba sin marcar — parecía "terminado" sin ninguna
 * pista de qué fallaba, y el tablero nunca disparaba la victoria.
 */
function analyzeBoard(puzzle: FillominoPuzzle, cells: number[]) {
  const { width, height } = puzzle;
  const total = width * height;
  const visited = Array(total).fill(false);
  const componentOf: number[] = Array(total).fill(-1);
  const componentSize: number[] = [];
  const componentValue: number[] = [];
  const componentClosed: boolean[] = [];
  let compId = 0;

  for (let i = 0; i < total; i++) {
    if (cells[i] === 0 || visited[i]) continue;
    const stack = [i];
    const comp = [i];
    visited[i] = true;
    let closed = true;
    while (stack.length) {
      const c = stack.pop()!;
      for (const n of getNeighbors(c, width, height)) {
        if (cells[n] === 0) closed = false;
        else if (cells[n] === cells[i] && !visited[n]) { visited[n] = true; comp.push(n); stack.push(n); }
      }
    }
    for (const c of comp) componentOf[c] = compId;
    componentSize[compId] = comp.length;
    componentValue[compId] = cells[i];
    componentClosed[compId] = closed;
    compId++;
  }

  const errorCells = new Set<number>();
  const closedCorrect = new Set<number>();
  for (let cid = 0; cid < compId; cid++) {
    const tooBig = componentSize[cid] > componentValue[cid];
    const tooSmallAndStuck = componentSize[cid] < componentValue[cid] && componentClosed[cid];
    if (tooBig || tooSmallAndStuck) {
      for (let i = 0; i < total; i++) if (componentOf[i] === cid) errorCells.add(i);
    } else if (componentSize[cid] === componentValue[cid]) {
      for (let i = 0; i < total; i++) if (componentOf[i] === cid) closedCorrect.add(i);
    }
  }

  // tamaño/valor del grupo de cada celda no vacía, para la insignia del
  // modo asistido ("2/4" = grupo abierto con 2 de las 4 casillas que le
  // corresponden). Solo tiene sentido en grupos aún ABIERTOS: uno cerrado
  // ya está resuelto (correcto o en rojo) y no necesita contador.
  const openBadge: Record<number, string> = {};
  for (let i = 0; i < total; i++) {
    const cid = componentOf[i];
    if (cid === -1 || componentClosed[cid]) continue;
    openBadge[i] = `${componentSize[cid]}/${componentValue[cid]}`;
  }

  return { componentOf, errorCells, closedCorrect, openBadge };
}

function getGroupBorderClasses(
  componentOf: number[], closedCorrect: Set<number>, index: number, width: number, height: number
): string {
  if (!closedCorrect.has(index)) return '';
  const row = Math.floor(index / width);
  const col = index % width;
  const cid = componentOf[index];
  let cls = '';
  if (row === 0 || componentOf[index - width] !== cid) cls += ' border-t-4';
  if (row === height - 1 || componentOf[index + width] !== cid) cls += ' border-b-4';
  if (col === 0 || componentOf[index - 1] !== cid) cls += ' border-l-4';
  if (col === width - 1 || componentOf[index + 1] !== cid) cls += ' border-r-4';
  return cls;
}

export default function FillominoGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dif = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('fillomino', dif);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const [puzzle, setPuzzle] = useState<FillominoPuzzle | null>(null);
  const [cells, setCells] = useState<number[]>([]);
  const [history, setHistory] = useState<number[][]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [selected, setSelected] = useState<number | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moveCount, setMoveCount] = useState(0);
  const [forceNew, setForceNew] = useState(0);
  const [hintCell, setHintCell] = useState<number | null>(null);

  useEffect(() => {
    if (isCompleted) return;
    const interval = setInterval(() => setElapsedTime((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isCompleted]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const loadPuzzle = useCallback(async (isNew = false) => {
    setLoading(true);
    setError('');
    try {
      const urlSeed = !isNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;
      const response = await api.get('/fillomino/generate', {
        params: { dificultad: dif, ...(seedToUse && { seed: seedToUse }) },
      });
      const data: FillominoPuzzle = response.data.data;
      setPuzzle(data);
      const initial = [...data.clues];
      setCells(initial);
      setHistory([[...initial]]);
      setHistIdx(0);
      setSelected(null);
      setMoveCount(0);
      setElapsedTime(0);
      setIsCompleted(false);
      setShowVictory(false);
      setHintCell(null);
      setModoAsistido(false);
      setSearchParams({ seed: data.seed.toString() }, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al generar el puzzle');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dif, forceNew]);

  useEffect(() => { loadPuzzle(forceNew > 0); }, [loadPuzzle]);

  const saveHistoryState = (next: number[]) => {
    const snap = [...next];
    const newHist = history.slice(0, histIdx + 1);
    newHist.push(snap);
    if (newHist.length > 200) newHist.shift();
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
  };

  const applyCells = (next: number[]) => {
    if (!puzzle) return;
    setCells(next);
    setMoveCount((m) => m + 1);
    saveHistoryState(next);

    const complete = next.every((v, i) => v === puzzle.solution[i]);
    if (complete) {
      setIsCompleted(true);
      setShowVictory(true);
      saveGameProgress({
        tiempo: elapsedTime,
        movimientos: moveCount + 1,
        // Identifica el tablero jugado: es lo que permite no repetírselo al
        // usuario cuando el nivel se sirve del almacén de pregenerados.
        tablero_id: puzzle.seed,
      });
      if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: moveCount + 1, completado: true });
    }
  };

  const setCellValue = (index: number, value: number) => {
    if (!puzzle || isCompleted) return;
    if (puzzle.clues[index] !== 0) return; // pista fija, no editable
    const next = [...cells];
    next[index] = value;
    applyCells(next);
  };

  const onCellClick = (index: number) => {
    if (!puzzle || isCompleted) return;
    if (puzzle.clues[index] !== 0) return;
    setSelected(index);
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (selected === null || !puzzle || isCompleted) return;
      if (e.key >= '1' && e.key <= '9') {
        const v = parseInt(e.key);
        if (v <= Math.max(...puzzle.solution)) setCellValue(selected, v);
      } else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') {
        setCellValue(selected, 0);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, puzzle, isCompleted, cells]);

  const onUndo = () => {
    if (histIdx <= 0) return;
    setCells([...history[histIdx - 1]]);
    setHistIdx(histIdx - 1);
  };

  const onRedo = () => {
    if (histIdx >= history.length - 1) return;
    setCells([...history[histIdx + 1]]);
    setHistIdx(histIdx + 1);
  };

  const onReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    const initial = [...puzzle.clues];
    setCells(initial);
    saveHistoryState(initial);
    setIsCompleted(false);
    setShowVictory(false);
    setHintCell(null);
    setSelected(null);
  };

  const onNewBoard = () => {
    setSearchParams({});
    setForceNew((f) => f + 1);
  };

  const handleHint = async () => {
    if (!puzzle || isCompleted) return;
    try {
      const { data: { data: { hint } } } = await api.post('/fillomino/hint', {
        board: cells, solution: puzzle.solution,
      });
      if (hint) {
        const next = [...cells];
        next[hint.index] = hint.value;
        setHintCell(hint.index);
        setTimeout(() => setHintCell(null), 1200);
        applyCells(next);
      }
    } catch { /* ignore */ }
  };

  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    applyCells([...puzzle.solution]);
  };

  const { componentOf, errorCells, closedCorrect, openBadge } = puzzle
    ? analyzeBoard(puzzle, cells)
    : { componentOf: [] as number[], errorCells: new Set<number>(), closedCorrect: new Set<number>(), openBadge: {} as Record<number, string> };

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Generando puzzle…</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-4">⚠️</div>
          <p>{error || 'Error desconocido'}</p>
          <button onClick={onNewBoard} className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg">
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  const cellSize = getBoardCellSizeFromArea(puzzle.width);
  const fontSize = getBoardFontSizeFromArea(puzzle.width, 0.5);
  const maxValue = Math.max(...puzzle.solution);
  const padEnabled = selected !== null && !isCompleted;

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🔢 Fillomino</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map((d) => (
            <button
              key={d.orden}
              onClick={() => { if (dif === d.orden) onNewBoard(); else navigate(`/juegos/fillomino/${d.orden}`); }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dif === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white/70 text-xs hidden sm:inline">⏱ {formatTime(elapsedTime)}</span>
        <div className="flex gap-1 items-center">
          <button onClick={onUndo} disabled={histIdx <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={onRedo} disabled={histIdx >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          <button onClick={() => setModoAsistido((v) => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={onReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={onNewBoard} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 flex items-center justify-center p-2" style={{ containerType: 'size' }}>
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${puzzle.width}, ${cellSize})`,
            gridTemplateRows: `repeat(${puzzle.height}, ${cellSize})`,
          }}
        >
          {Array.from({ length: puzzle.width * puzzle.height }, (_, i) => {
            const isClue = puzzle.clues[i] !== 0;
            const value = cells[i];
            const hasError = errorCells.has(i);
            const isHint = hintCell === i;
            const isSelected = selected === i;

            let bg = '#ffffff';
            if (isCompleted) bg = '#dcfce7';
            else if (hasError) bg = '#fecaca';
            else if (isHint) bg = '#fde68a';
            else if (isSelected) bg = '#e0e7ff';

            let color = '#16a34a'; // verde: introducido por el jugador
            if (isClue) color = '#111827'; // negro: pista fija, no editable
            if (hasError) color = '#991b1b'; // rojo: conflicto de tamaño de grupo

            return (
              <button
                key={i}
                onClick={() => onCellClick(i)}
                disabled={isCompleted || isClue}
                className={`relative flex items-center justify-center transition-all border border-slate-300 ${
                  getGroupBorderClasses(componentOf, closedCorrect, i, puzzle.width, puzzle.height)
                } ${isSelected ? 'ring-2 ring-indigo-500 z-10' : ''}`}
                style={{ width: cellSize, height: cellSize, backgroundColor: bg, borderColor: '#1e293b' }}
              >
                {value !== 0 && (
                  <span className="select-none font-bold" style={{ fontSize, color }}>
                    {value}
                  </span>
                )}
                {modoAsistido && value !== 0 && openBadge[i] && (
                  <span
                    className="absolute select-none font-semibold"
                    style={{ bottom: 1, right: 3, fontSize: `calc(${fontSize} * 0.32)`, color: hasError ? '#991b1b' : '#64748b' }}
                  >
                    {openBadge[i]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* La barra se renderiza siempre: si apareciera solo al seleccionar, el
          tablero (centrado en el hueco restante) daria un salto cada vez. */}
      <div className={`bg-black/20 backdrop-blur-sm px-3 pt-2 pb-safe shrink-0 flex items-center justify-center gap-1.5 flex-wrap transition-opacity ${padEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
        {Array.from({ length: maxValue }, (_, k) => k + 1).map((v) => (
          <button
            key={v}
            onClick={() => { if (padEnabled) setCellValue(selected!, v); }}
            disabled={!padEnabled}
            className="w-11 h-11 rounded-md bg-white/90 hover:bg-white text-purple-700 font-bold text-lg transition-all"
          >
            {v}
          </button>
        ))}
        <button
          onClick={() => { if (padEnabled) setCellValue(selected!, 0); }}
          disabled={!padEnabled}
          title="Borrar"
          className="w-11 h-11 ml-2 flex items-center justify-center rounded-md bg-red-500 hover:bg-red-600 text-white font-bold text-base shadow active:scale-95 transition-all"
        >
          ✕
        </button>
      </div>

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: elapsedTime, movimientos: moveCount }}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Fillomino"
        gameIcon="🔢"
        objective="Completa el tablero colocando números agrupados por cantidad: el número 3 siempre estará en grupos de 3 casillas contiguas, el número 4 en grupos de 4, etc."
        rules={[
          'Cada número indica el tamaño del grupo de casillas contiguas (arriba/abajo/izquierda/derecha) al que pertenece esa casilla.',
          'Todas las casillas de un mismo grupo muestran el mismo número, y ese número es exactamente el tamaño del grupo.',
          'Dos grupos vecinos nunca tienen el mismo número.',
          'Algunos números aparecen ya dados en negro y no se pueden modificar. El resto se rellena jugando.',
        ]}
        howToPlay="Toca una casilla vacía para seleccionarla y luego elige un número del teclado que aparece abajo (o pulsa una tecla numérica). Al completar un grupo correctamente, se le dibuja un borde negro alrededor. Si un número provoca un conflicto con sus vecinos, se resalta en rojo. El modo asistido (escudo) añade un pequeño contador tamaño/valor en cada grupo que aún puedes ampliar."
        author="Jonas Kölker / Simon Tatham"
      />
    </div>
  );
}
